import ValidationError from "../../../services/exceptions/validation-error";
import ValidationType from "../../../services/exceptions/validation-type";
import FieldAreaType from "../enums/field-area-type";

const error = (message) => new ValidationError(message, ValidationType.Error);

/**
 * Validate the field-marker portions of a game configuration's `general`
 * block. Throws a ValidationError describing the first problem found. Configs
 * that do not use markers (e.g. traditional football) pass through untouched.
 *
 * @param {object} general gameConfiguration.general
 */
export const validateFieldConfig = (general) => {
    if (!general) {
        return;
    }
    const {
        fieldLength,
        firstDownYards,
        firstDownYardSet,
        customFirstDownYardSet,
        fieldAreaSet,
        redZoneLength
    } = general;

    // Yard-to-gain chain distance (used when firstDownFlagStyle <= 0).
    if (
        typeof firstDownYards === "number" &&
        (firstDownYards < 1 || firstDownYards > fieldLength)
    ) {
        throw error(
            `First down yards ${firstDownYards} must be between 1 and ${fieldLength}.`
        );
    }

    // Only validate the spot list when the crew has authored it. Otherwise it
    // is auto-recomputed uniformly at game start (setStartSettings), so its
    // stored value can legitimately be stale for the current field length.
    if (customFirstDownYardSet && Array.isArray(firstDownYardSet)) {
        let previous = -1;
        firstDownYardSet.forEach((yard) => {
            if (typeof yard !== "number" || yard < 0 || yard > fieldLength) {
                throw error(
                    `First down spot ${yard} must be between 0 and ${fieldLength}.`
                );
            }
            if (yard <= previous) {
                throw error("First down spots must be sorted and unique.");
            }
            previous = yard;
        });
    }

    if (Array.isArray(fieldAreaSet)) {
        const sorted = [...fieldAreaSet].sort((a, b) => a.start - b.start);
        let previousEnd = 0;
        sorted.forEach(({ start, end }) => {
            if (
                typeof start !== "number" ||
                typeof end !== "number" ||
                start < 0 ||
                end > fieldLength ||
                start >= end
            ) {
                throw error(
                    `Field area (${start}-${end}) must fall within 0 and ${fieldLength}.`
                );
            }
            if (start < previousEnd) {
                throw error("Field areas cannot overlap.");
            }
            previousEnd = end;
        });
    }

    if (
        typeof redZoneLength === "number" &&
        (redZoneLength < 0 || redZoneLength > fieldLength)
    ) {
        throw error(
            `Red zone length ${redZoneLength} must be between 0 and ${fieldLength}.`
        );
    }
};

/**
 * Keep redZoneLength consistent with any configured red-zone field area so the
 * existing red-zone stat path (isInRedZone / getRedzoneStatistics) reflects the
 * crew's configuration. The low-end red zone (anchored at the goal line) drives
 * the symmetric redZoneLength; falls back to the high-end zone if only that one
 * is present. Returns a new gameConfiguration; the input is not mutated.
 *
 * @param {object} gameConfiguration
 * @returns {object}
 */
/**
 * PSTATS-1147: clock settings moved from general (clockType) into their own
 * clock section. Lift legacy values off configs saved before the move so
 * readers and the settings form only ever see the clock section. Returns a
 * new gameConfiguration; the input is not mutated. No-op for configs that
 * already have a clock section (or none of the legacy keys).
 *
 * @param {object} gameConfiguration
 * @returns {object}
 */
export const migrateClockConfig = (gameConfiguration) => {
    let result = gameConfiguration;
    const general = gameConfiguration?.general;
    if (!gameConfiguration?.clock && general?.clockType !== undefined) {
        const { clockType, runningClock, ...restOfGeneral } = general;
        result = {
            ...gameConfiguration,
            general: restOfGeneral,
            clock: {
                clockType,
                ...(runningClock !== undefined ? { runningClock } : {})
            }
        };
    }
    return result;
};

export const normalizeFieldConfig = (gameConfiguration) => {
    const general = gameConfiguration?.general;
    if (!general || !Array.isArray(general.fieldAreaSet)) {
        return gameConfiguration;
    }

    const { fieldLength } = general;

    // Clamp field areas to the field: default areas are authored for the 80yd
    // field, so a smaller fieldLength (preset/override) would otherwise leave
    // out-of-range areas that fail validation. Drop areas entirely past the
    // goal line; clamp the trailing end onto it.
    const fieldAreaSet = general.fieldAreaSet
        .filter((area) => area.start < fieldLength)
        .map((area) => ({ ...area, end: Math.min(area.end, fieldLength) }));

    const redZones = fieldAreaSet.filter(
        (area) => area.type === FieldAreaType.RedZone.code
    );

    const lowEnd = redZones.find((area) => area.start === 0);
    const redZoneLength = redZones.length
        ? lowEnd
            ? lowEnd.end
            : fieldLength - redZones[0].start
        : general.redZoneLength;

    return {
        ...gameConfiguration,
        general: {
            ...general,
            fieldAreaSet,
            redZoneLength
        }
    };
};
